import React, { useState } from "react";
import SelectionTable from "./common/SelectionTable";
import CrudModal from "../general/CrudModal";
import { API_ENDPOINTS } from "../../../config/apiConfig";
import axios from "axios";

const ChargingStationSelection = ({ 
  chargingStations, 
  loading, 
  error, 
  selectedChargingStations, 
  setSelectedChargingStations, 
  nextStep, 
  prevStep,
  refreshChargingStations 
}) => {
  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  
  // Handle checkbox change
  const handleCheckboxChange = (stationIdOrArray, e) => {
    // Stop propagation if event is provided
    if (e) {
      e.stopPropagation();
    }
    
    setSelectedChargingStations((prevSelected) => {
      // Handle array of IDs (for select all/none functionality)
      if (Array.isArray(stationIdOrArray)) {
        return stationIdOrArray;
      }
      // Handle single ID toggle
      const stationId = stationIdOrArray;
      if (prevSelected.includes(stationId)) {
        return prevSelected.filter((id) => id !== stationId);
      } else {
        return [...prevSelected, stationId];
      }
    });
  };

  // Handle "Select All" checkbox
  const handleSelectAllStations = (e) => {
    if (e) e.stopPropagation();
    if (allStationsSelected) {
      // Deselect all stations
      handleCheckboxChange([]);
    } else {
      // Select all stations at once
      handleCheckboxChange(chargingStations.map(station => station.cs_id));
    }
  };

  const allStationsSelected = chargingStations.length > 0 && 
    selectedChargingStations.length === chargingStations.length;

  // Define columns for the charging station table
  const stationColumns = [
    { key: 'name', label: 'Name', width: '20%' },
    { key: 'owner', label: 'Owner', width: '15%' },
    { key: 'addr', label: 'Address', width: '23%' },
    { key: 'status', label: 'Status', width: '10%' },
    { key: 'max_power', label: 'Max Power', width: '10%' },
    { key: 'connectors', label: 'Connectors', width: '10%' }
  ];

  // Define how to render each row
  /**
   * TODO: Temporary workaround for table header/content misalignment
   * 
   * When the scrollbar appears for charging stations, it pushes the table body left,
   * causing the headers to become misaligned with the content. Currently using extra
   * spacing in content cells as a temporary fix, but this needs a proper solution.
   * 
   */
  const renderStationRow = (station) => (
    <>
      <td style={{ width: stationColumns[0].width }}>{station.name || "—"}</td>
      <td style={{ width: stationColumns[1].width }}>{"\u00A0" + station.owner || "—"}</td>
      <td style={{ width: stationColumns[2].width }}>{"\u00A0" + station.addr || `${station.lat}, ${station.lon}`}</td>
      <td style={{ width: stationColumns[3].width }}>{"\u00A0\u00A0" + station.status || "—"}</td>
      <td style={{ width: stationColumns[4].width }}>{"\u00A0\u00A0" + station.max_power} kW</td>
      <td style={{ width: stationColumns[5].width }}>{"\u00A0\u00A0\u00A0" + (station.accepted_connector_types || "—")}</td>
    </>
  );

  // Get the last updated time
  const allUpdatedTimes = chargingStations.map(station => station.last_updated); 
  const lastUpdatedTime = allUpdatedTimes.sort((a, b) => new Date(b) - new Date(a))[0];
  const formattedLastUpdatedTime = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(lastUpdatedTime));

  // Define editable fields for charging stations - based on ChargerTable.jsx fields
  const editableFields = [
    // Basic Information
    { id: 'name', name: 'Name', type: 'text', category: 'basic', required: true },
    { id: 'owner', name: 'Owner', type: 'text', category: 'basic' },
    { id: 'addr', name: 'Address', type: 'address', category: 'basic', required: true },
    // { id: 'lat', name: 'Latitude', type: 'number', category: 'basic', tooltip: 'Geographic latitude coordinate' },
    // { id: 'lon', name: 'Longitude', type: 'number', category: 'basic', tooltip: 'Geographic longitude coordinate' },
    { id: 'status', name: 'Status', type: 'select', category: 'basic',
      options: [
        { value: 'Available', label: 'Available' },
        { value: 'Temporarily Unavailable', label: 'Temporarily Unavailable' },
        { value: 'Under Construction', label: 'Under Construction' },
        { value: 'Planned', label: 'Planned' }
      ]
    },
    { id: 'year_op', name: 'Year of Operation', type: 'number', category: 'basic' },
    
    // Charging Details
    { id: 'chargers', name: 'Charger Types', type: 'text', category: 'charging' },
    { id: 'powers', name: 'Power Ratings (kW)', type: 'text', category: 'charging', tooltip: 'Available power ratings separated by comma (e.g., "50, 150, 350")' },
    { id: 'max_power', name: 'Maximum Power (kW)', type: 'number', category: 'charging', required: true },
    { id: 'stall_count', name: 'Stall Count', type: 'number', category: 'charging' },
    { id: 'plug_count', name: 'Plug Count', type: 'number', category: 'charging' },
    
    // Access Information
    { id: 'type_of_access', name: 'Type of Access', type: 'text', category: 'access' },
    { id: 'facility_type', name: 'Facility Type', type: 'text', category: 'access' },
    { id: 'eligible_vehicles', name: 'Eligible Vehicles', type: 'text', category: 'access' },
    { id: 'ev_network', name: 'EV Network', type: 'text', category: 'access' },
    { id: 'vehicle_classes', name: 'Vehicle Classes', type: 'text', category: 'access' },
    { id: 'pullthrough', name: 'Pull Through', type: 'checkbox', category: 'access' },
    { id: 'domicile_at_site', name: 'Domicile at Site', type: 'checkbox', category: 'access' },
    
    // Payment and Contact
    { id: 'accepted_payment', name: 'Accepted Payment', type: 'text', category: 'payment' },
    { id: 'contact_name', name: 'Contact Name', type: 'text', category: 'payment' },
    { id: 'contact_email', name: 'Contact Email', type: 'text', category: 'payment' },
    { id: 'facility_phone', name: 'Facility Phone', type: 'text', category: 'payment' },
    
    // Additional Information
    { id: 'hours_of_operation', name: 'Hours of Operation', type: 'text', category: 'additional' },
    { id: 'fuel_type', name: 'Fuel Type', type: 'text', category: 'additional' },
    { id: 'notes', name: 'Notes', type: 'textarea', category: 'additional' },
    { id: 'additional_info', name: 'Additional Info', type: 'text', category: 'additional' }
  ];

  // Handle edit button click
  const handleEditStation = (station) => {
    setCurrentEditItem(station);
    setIsEditModalOpen(true);
    setEditError(null);
  };

  // Add handlers similar to what's in DepotSelection.js for adding/editing/deleting charging stations
  // Example for editing a charging station:
  const handleSaveChanges = async (updatedStation) => {
    setEditLoading(true);
    setEditError(null);
    
    try {
      // Implementation omitted for brevity
      // ...
      
      // Call the refreshChargingStations function after successful update
      if (typeof refreshChargingStations === 'function') {
        refreshChargingStations();
      }
      
      // Close the modal
      setIsEditModalOpen(false);
      setCurrentEditItem(null);
    } catch (error) {
      console.error("Error updating charging station:", error);
      setEditError(error.response?.data?.error || error.message || "An error occurred while updating the charging station");
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return <div className="step-container"><p>Loading charging stations...</p></div>;
  }

  if (error) {
    return <div className="step-container"><p className="error">{error}</p></div>;
  }

  return (
    <div className="step-container">
      {chargingStations.length === 0 ? (
        <p>No charging stations found. Please add charging stations to continue.</p>
      ) : (
        <>
          <div className="flex justify-between items-center">
            <p className="text-sm">
              Showing {chargingStations.length} charging stations 
              ({selectedChargingStations.length} selected)
            </p>
            <p className="text-sm text-right">
              Last updated: {formattedLastUpdatedTime}
            </p>
          </div>
          <SelectionTable
            items={chargingStations}
            selectedItems={selectedChargingStations}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAllStations}
            allSelected={allStationsSelected}
            columns={stationColumns}
            itemIdKey="cs_id"
            renderRow={renderStationRow}
            onEditItem={handleEditStation}
            actionColumnLabel="View"
          />
        </>
      )}

      {/* Edit modal */}
      {isEditModalOpen && currentEditItem && (
        <div className="modal-overlay">
          <CrudModal
            entity="Charging Station"
            mode="edit"
            fields={editableFields}
            selectedItems={[currentEditItem]}
            initialData={currentEditItem}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            isLoading={editLoading}
            error={editError}
            onSaveChanges={handleSaveChanges}
            hideUpdateButton={true}
          />
        </div>
      )}
    </div>
  );
};

export default ChargingStationSelection; 